import {
  FormField,
  Button,
  Option,
  FormControl,
  IconButton,
  NavigateWithoutBlocker,
  useFormContext,
} from 'platform/components';
import {Box, HStack, Space, Text, VStack} from 'platform/foundation';

import {useFieldArray, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';
import {testIds, settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';

import {TireTransitFormType} from '../schemas/tireTransitFormSchema';

export type TireTransitFormFieldsProps = {
  control: FormControl<TireTransitFormType>;
  getBranchOptions: () => Option[];
  groupedSeries: Record<string, Option[]>;
  isSeriesLoading: boolean;
  navigateWithoutBlocker: NavigateWithoutBlocker;
};

export function TireTransitFormFields({
  control,
  getBranchOptions,
  groupedSeries,
  isSeriesLoading,
  navigateWithoutBlocker,
}: TireTransitFormFieldsProps) {
  const {setValue} = useFormContext<TireTransitFormType>();
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'destinationBranch',
  });

  const homeBranchId = useWatch({
    control,
    name: 'homeBranchId',
  });

  const destinationBranches = useWatch({
    control,
    name: 'destinationBranch',
  });

  const isHomeBranchSelected = Boolean(homeBranchId);

  const getFilteredDestinationBranchOptions = (currentIndex?: number): Option[] => {
    const allBranches = getBranchOptions();
    const selectedBranchIds =
      destinationBranches?.map((branch) => branch.branchId).filter(Boolean) || [];

    return allBranches.filter((option) => {
      // Filter out already selected branches, except current field
      if (currentIndex !== undefined) {
        const otherSelectedBranches = selectedBranchIds.filter(
          (_, index) => index !== currentIndex
        );
        return !otherSelectedBranches.includes(option.value);
      }

      return !selectedBranchIds.includes(option.value);
    });
  };

  const handleDefaultToggle = (currentIndex: number, isChecked: boolean) => {
    if (isChecked) {
      // Uncheck all other defaults
      fields.forEach((_, index) => {
        if (index !== currentIndex) {
          setValue(`destinationBranch.${index}.isDefault`, false);
        }
      });
    }
  };

  return (
    <Box paddingBottom={24}>
      <VStack spacing={6}>
        <Box width="100%" maxWidth={90}>
          <FormField
            control={control}
            type="choice"
            name="homeBranchId"
            label={i18n.t('entity.tireTransit.labels.homeBranch')}
            options={getBranchOptions()}
            isRequired
            data-testid={testIds.settings.tireTransit('homeBranch')}
          />
        </Box>

        <Text size="small">{i18n.t('entity.tireTransit.labels.description')}</Text>

        <VStack align="flex-start" spacing={4}>
          {fields.map((field, index) => (
            <HStack key={field.id} align="flex-start" spacing={4}>
              <Box width={60}>
                <FormField
                  control={control}
                  type="choice"
                  name={`destinationBranch.${index}.branchId`}
                  label={i18n.t('entity.tireTransit.labels.destinationBranch')}
                  options={getFilteredDestinationBranchOptions(index)}
                  isRequired
                  isDisabled={!isHomeBranchSelected}
                  data-testid={testIds.settings.tireTransit(`destinationBranch-${index}-branchId`)}
                />
              </Box>
              <Box width={60}>
                <FormField
                  control={control}
                  type="choice"
                  name={`destinationBranch.${index}.docSeriesId`}
                  label={i18n.t('entity.documentSeries.labels.documentSeries')}
                  options={groupedSeries['pneu-hotel/tire-transport-document']}
                  isLoading={isSeriesLoading}
                  isRequired
                  isDisabled={!isHomeBranchSelected}
                  data-testid={testIds.settings.tireTransit(
                    `destinationBranch-${index}-docSeriesId`
                  )}
                />
              </Box>
              <Box width={60}>
                <FormField
                  control={control}
                  type="number"
                  isStepperVisible
                  minStepperValue={0}
                  name={`destinationBranch.${index}.transitTimeDays`}
                  label={i18n.t('entity.tireTransit.labels.transitTimeDays')}
                  isRequired
                  isDisabled={!isHomeBranchSelected}
                  data-testid={testIds.settings.tireTransit(
                    `destinationBranch-${index}-transitTimeDays`
                  )}
                />
              </Box>
              <HStack spacing={2} align="center">
                <VStack>
                  <Space vertical={7} />
                  <FormField
                    control={control}
                    type="checkbox"
                    name={`destinationBranch.${index}.isDefault`}
                    label={i18n.t('general.labels.default')}
                    isDisabled={!isHomeBranchSelected}
                    onChange={(isChecked) => handleDefaultToggle(index, isChecked)}
                    data-testid={testIds.settings.tireTransit(
                      `destinationBranch-${index}-isDefault`
                    )}
                  />
                </VStack>

                <Space horizontal={2} />

                <VStack>
                  <Space vertical={7} />
                  <IconButton
                    icon="action/delete"
                    size="small"
                    severity="danger"
                    onClick={() => remove(index)}
                    isDisabled={!isHomeBranchSelected || (index === 0 && fields.length === 1)}
                    data-testid={testIds.settings.tireTransit(`destinationBranch-${index}-remove`)}
                  />
                </VStack>
              </HStack>
            </HStack>
          ))}

          <Button
            leftIcon="content/add_circle"
            variant="link"
            size="small"
            title={i18n.t('entity.tireTransit.labels.addAnother')}
            onClick={() =>
              append({
                branchId: '',
                docSeriesId: '',
                transitTimeDays: 0,
                isDefault: false,
              })
            }
            isDisabled={!isHomeBranchSelected}
            data-testid={testIds.settings.tireTransit('addDestinationBranch')}
          />
        </VStack>

        <SettingsFooter
          actions={[
            {
              type: 'button',
              variant: 'secondary',
              title: i18n.t('general.actions.discardChanges'),
              onClick: () => navigateWithoutBlocker(settingsRoutes.tireTransit),
            },
            {
              control,
              type: 'form-button',
              buttonType: 'submit',
              title: i18n.t('general.actions.save'),
            },
          ]}
        />
      </VStack>
    </Box>
  );
}
